import React, { useState, useEffect, useCallback } from "react";
import AuthService from "../../services/AuthService";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../../services/ApiService";
import AreaChart from "../../components/AreaChart";
import DonutChart from "../../components/DonutChart";
import Loader from "../../components/Loader";
import BookingDetailsOffcanvas from "../../components/BookingDetailsOffcanvas";

import "./Dashboard.css";

const fmtNumber = (value) => Number(value || 0).toLocaleString();
const fmtCurrency = (value) => `AED ${Number(value || 0).toLocaleString()}`;

// Colour ring for the Room Utilisation donut — cycled across however many rooms exist.
const ROOM_COLORS = ["#78BBF9", "#9CAF6A", "#E0B450", "#B08FD8", "#E08A76", "#5FC2C2", "#EC8AB6"];

const STATUS_META = [
  { key: "active",    label: "Active",    color: "#78BBF9", field: "active_bookings" },
  { key: "completed", label: "Completed", color: "#9CAF6A", field: "completed_bookings" },
  { key: "cancelled", label: "Cancelled", color: "#E08A76", field: "cancelled_bookings" },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();
  const adminName = currentUser?.name || "Admin";

  const [isLoader, setIsLoader] = useState(false);
  const [stats, setStats] = useState({});
  const [charts, setCharts] = useState({ labels: [], bookings: [], users: [], memberships: [] });
  const [topServices, setTopServices] = useState([]);
  const [topRooms, setTopRooms] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [viewFormat, setViewFormat] = useState("monthly");
  // Recent Bookings opens the same slide-in panel as the bookings list rather
  // than navigating away from the dashboard.
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);

  const getDashboard = useCallback(async () => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: "GET",
        url: `admin/getDashboard?view_format=${viewFormat}`,
      });

      const data = response.data;
      if (data.status) {
        setStats(data.data.stats || {});
        setCharts(data.data.charts || {});
        setTopServices(data.data.topServices || []);
        setTopRooms(data.data.topRooms || []);
        setRecentBookings(data.data.recentBookings || []);
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  }, [viewFormat]);

  useEffect(() => {
    if (!AuthService.getCurrentUser()) {
      navigate("/admin/login");
    }
    getDashboard();
  }, [navigate, getDashboard]);

  const periodLabel = stats?.period_label || "last period";

  const statCards = [
    {
      label: "Total Bookings",
      value: fmtNumber(stats?.total_bookings),
      change: stats?.total_bookings_change,
      icon: "bi-calendar2-check",
      tone: "tone-green",
      onClick: () => navigate("/admin/bookings"),
    },
    {
      label: "Total Users",
      value: fmtNumber(stats?.total_users),
      change: stats?.total_users_change,
      icon: "bi-people-fill",
      tone: "tone-amber",
      onClick: () => navigate("/admin/users"),
    },
    {
      label: "New Memberships",
      value: fmtNumber(stats?.new_memberships),
      change: stats?.memberships_change,
      icon: "bi-award",
      tone: "tone-tan",
      onClick: () => navigate("/admin/membership-plans/subscribers"),
    },
    {
      label: "Cancelled Memberships",
      value: fmtNumber(stats?.cancelled_memberships),
      change: stats?.cancelled_memberships_change,
      icon: "bi-x-circle",
      tone: "tone-violet",
      onClick: () => navigate("/admin/membership-plans/subscribers"),
    },
    {
      label: "Membership Revenue",
      value: fmtCurrency(stats?.membership_revenue),
      icon: "bi-currency-dollar",
      tone: "tone-violet",
      onClick: () => navigate("/admin/membership-plans"),
    },
  ];

  const statusSegments = STATUS_META
    .map((m) => ({ ...m, value: Number(stats?.[m.field]) || 0 }))
    .filter((s) => s.value > 0);
  const statusTotal = statusSegments.reduce((sum, s) => sum + s.value, 0);

  const roomSegments = topRooms
    .map((r, i) => ({
      key: r._id || r.title || i,
      label: r.title || "—",
      value: Number(r.totalBookings) || 0,
      color: ROOM_COLORS[i % ROOM_COLORS.length],
    }))
    .filter((s) => s.value > 0);
  const roomTotal = roomSegments.reduce((sum, s) => sum + s.value, 0);

  // A booking can carry several services, so list them all rather than only the
  // first. Falls back to a dash when none are attached.
  const serviceName = (booking) => {
    const titles = (booking.services || [])
      .map((s) => s.service_id?.title)
      .filter(Boolean);
    return titles.length > 0 ? titles.join(", ") : "—";
  };

  const handleBookingClick = (booking) => {
    setSelectedBookingId(booking._id);
    setShowBookingDetails(true);
  };

  const handleCloseOffcanvas = () => {
    setShowBookingDetails(false);
    setSelectedBookingId(null);
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  };

  const statusBadgeClass = (status) => {
    const map = {
      pending: "status-pill status-pending",
      confirmed: "status-pill status-confirmed",
      completed: "status-pill status-completed",
      cancelled: "status-pill status-cancelled",
    };
    return map[status?.toLowerCase()] || "status-pill status-default";
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-left">
          <h1 className="page-title">Welcome back, {adminName} 👋</h1>
          <p className="page-subtitle">Here's what's happening today.</p>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            {["weekly", "monthly", "yearly"].map((v) => (
              <button
                key={v}
                className={`toggle-btn ${viewFormat === v ? "active" : ""}`}
                onClick={() => setViewFormat(v)}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {isLoader ? (
          <div className="loader-container">
            <Loader text="Loading Dashboard Data..." size="40" />
          </div>
        ) : (
          <>
            {/* Key metrics */}
            <div className="stat-grid">
              {statCards.map((card) => {
                const hasChange = card.change !== undefined && card.change !== null;
                const positive = Number(card.change) >= 0;
                return (
                  <div
                    key={card.label}
                    className="stat-card"
                    onClick={card.onClick}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="stat-card-top">
                      <div className={`stat-icon  ${card.tone}`}>
                        <i className={`bi fs-1 text-dark ${card.icon}`}></i>
                      </div>
                      <div className="stat-meta">
                        <p className="stat-label">{card.label}</p>
                        <h3 className="stat-value">{card.value}</h3>
                      </div>
                    </div>
                    {hasChange && (
                      <div className="stat-change">
                        <span className={`change-pill ${positive ? "up" : "down"}`}>
                          <i className={`bi bi-arrow-${positive ? "up" : "down"}`}></i>
                          {positive ? "+" : ""}{card.change}%
                        </span>
                        <span className="change-period">vs {periodLabel}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bookings overview + status + top services */}
            <div className="dash-row">
              <div className="dash-card flex-bookings">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Bookings Overview</h3>
                </div>
                <div className="dash-card-body chart-area">
                  <AreaChart
                    labels={charts.labels}
                    label="Bookings"
                    dataset={charts.bookings}
                  />
                </div>
              </div>

              <div className="dash-card flex-status">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Bookings Status</h3>
                </div>
                <div className="dash-card-body status-body">
                  {statusTotal > 0 ? (
                    <>
                      <div className="donut-wrap">
                        <DonutChart
                          data={statusSegments.map((s) => s.value)}
                          labels={statusSegments.map((s) => s.label)}
                          colors={statusSegments.map((s) => s.color)}
                        />
                        <div className="donut-center">
                          <span className="donut-total">{fmtNumber(statusTotal)}</span>
                          <span className="donut-total-label">Total</span>
                        </div>
                      </div>
                      <ul className="status-legend">
                        {statusSegments.map((s) => {
                          const pct = ((s.value / statusTotal) * 100).toFixed(1);
                          return (
                            <li key={s.key}>
                              <span className="legend-dot" style={{ background: s.color }}></span>
                              <span className="legend-label">{s.label}</span>
                              <span className="legend-value">
                                {fmtNumber(s.value)} <span className="legend-pct">({pct}%)</span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <div className="empty-state">No booking status data available</div>
                  )}
                </div>
              </div>

              <div className="dash-card flex-categories">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Top Services</h3>
                  <Link to="/admin/services" className="see-all-link">View All</Link>
                </div>
                <div className="dash-card-body">
                  <ul className="top-list">
                    {topServices.length > 0 ? (
                      topServices.slice(0, 5).map((service, index) => (
                        <li key={service._id || index} className="top-list-item">
                          <span className="top-list-icon">
                            <i className="bi bi-snow2"></i>
                          </span>
                          <span className="top-list-name">{service.title || "N/A"}</span>
                          <span className="top-list-count">{fmtNumber(service.totalBookings)}</span>
                        </li>
                      ))
                    ) : (
                      <li className="empty-state">No services data available</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Recent bookings + rooms */}
            <div className="dash-row">
              <div className="dash-card flex-recent">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Recent Bookings</h3>
                  <Link to="/admin/bookings" className="see-all-link">View All</Link>
                </div>
                <div className="dash-card-body">
                  <div className="table-responsive">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Booking ID</th>
                          <th>Customer</th>
                          <th>Service</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Paid By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentBookings.length > 0 ? (
                          recentBookings.map((b, i) => (
                            <tr
                              key={b._id || i}
                              onClick={() => handleBookingClick(b)}
                              style={{ cursor: "pointer" }}
                            >
                              <td className="booking-code">
                                {b.booking_code || `#${b._id?.toString().slice(-6)}`}
                              </td>
                              <td>{b.user_id?.name || b.guest_name || "N/A"}</td>
                              <td>{serviceName(b)}</td>
                              <td>{formatDate(b.booking_date || b.createdAt)}</td>
                              <td>
                                <span className={statusBadgeClass(b.status)}>
                                  {(b.status || "N/A").toUpperCase()}
                                </span>
                              </td>
                              <td className="text-capitalize">{b.payment_by || "—"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="empty-state">No recent bookings</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="dash-card flex-revenue">
                <div className="dash-card-header">
                  <h3 className="dash-card-title">Room Utilisation</h3>
                  <Link to="/admin/rooms" className="see-all-link">View All</Link>
                </div>
                <div className="dash-card-body status-body">
                  {roomTotal > 0 ? (
                    <>
                      <div className="donut-wrap">
                        <DonutChart
                          data={roomSegments.map((s) => s.value)}
                          labels={roomSegments.map((s) => s.label)}
                          colors={roomSegments.map((s) => s.color)}
                        />
                        <div className="donut-center">
                          <span className="donut-total">{fmtNumber(roomTotal)}</span>
                          <span className="donut-total-label">Bookings</span>
                        </div>
                      </div>
                      <ul className="status-legend">
                        {roomSegments.map((s) => {
                          const pct = ((s.value / roomTotal) * 100).toFixed(1);
                          return (
                            <li key={s.key}>
                              <span className="legend-dot" style={{ background: s.color }}></span>
                              <span className="legend-label">{s.label}</span>
                              <span className="legend-value">
                                {fmtNumber(s.value)} <span className="legend-pct">({pct}%)</span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <div className="empty-state">No room booking data available</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Same Booking Details panel the bookings list uses */}
      <BookingDetailsOffcanvas
        bookingId={selectedBookingId}
        show={showBookingDetails}
        onClose={handleCloseOffcanvas}
        onUpdate={() => getDashboard()}
      />
    </div>
  );
};

export default Dashboard;
